import { type DatabaseDiff } from '@databases/collab/y-database';
import { type CommentThreadJSON } from '@databases/model/commentThreads';
import { NumberSeparator } from '@databases/model/custom-number-format';
import {
  type Database as CollabDatabase,
  type DatabaseJSON,
} from '@databases/model/database';
import { type EntryJSON } from '@databases/model/entry';
import {
  BacklinkDisplayType,
  DisplayedDetailType,
  type FieldJSON,
  FieldType,
  NumberFormat,
  ParentPageType,
} from '@databases/model/field';
import {
  type AllValueJSON,
  type SingleAttachmentValue,
  type SingleEntryLinkValue,
  type SingleHyperlinkValue,
  type SingleJiraIssueValue,
  type SingleUserValue,
  UserType,
} from '@databases/model/value';
import {
  CollabCalculationTypes,
  CollabCardWidth,
  CollabCoverFieldType,
  CollabLayoutType,
  type DatabaseViewJSON,
} from '@databases/model/views/view';
import {
  type CollabFilterJSON,
  CollabFilterType,
} from '@databases/model/views/view-filter';
import {
  type CollabGroupingJSON,
  CollabGroupSortType,
} from '@databases/model/views/view-grouping';
import {
  CUSTOM_TEMPLATE_EMPTY,
  CUSTOM_TEMPLATE_ENTRY_MACRO,
  DEFAULT_ENTRY_ID,
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseFieldSettings,
  DatabaseFieldType,
  type DatabaseValue,
  type RawDatabaseValue,
  BacklinkDisplayType as ReduxBacklinkDisplayType,
  DisplayedDetailType as ReduxDisplayedDetailType,
  NumberFormats as ReduxNumberFormat,
  NumberSeparators as ReduxNumberSeparator,
  ParentPageType as ReduxParentPageType,
  USER_VALUE_CURRENT,
} from '@databases/types/database';
import {
  DatabaseCalculationType,
  DatabaseCardWidth,
  type DatabaseFilter,
  DatabaseFilterType,
  DatabaseGroupSortType,
  DatabaseLayoutType,
  type DatabaseView,
  databaseViewSettingsValidator,
} from '@databases/types/database/view';

export const fieldTypeMap: Record<FieldType, DatabaseFieldType> = {
  [FieldType.Text]: DatabaseFieldType.TEXT,
  [FieldType.Date]: DatabaseFieldType.DATE,
  [FieldType.Number]: DatabaseFieldType.NUMBER,
  [FieldType.Checkbox]: DatabaseFieldType.CHECKBOX,
  [FieldType.Select]: DatabaseFieldType.SELECT,
  [FieldType.User]: DatabaseFieldType.USER,
  [FieldType.Attachment]: DatabaseFieldType.ATTACHMENT,
  [FieldType.PageLink]: DatabaseFieldType.PAGE_LINK,
  [FieldType.PageLabels]: DatabaseFieldType.PAGE_LABELS,
  [FieldType.PageStatus]: DatabaseFieldType.PAGE_STATUS,
  [FieldType.PageDetails]: DatabaseFieldType.PAGE_DETAILS,
  [FieldType.Hyperlink]: DatabaseFieldType.HYPERLINK,
  [FieldType.JiraIssue]: DatabaseFieldType.JIRA_ISSUE,
  [FieldType.JiraIssueDetails]: DatabaseFieldType.JIRA_ISSUE_DETAILS,
  [FieldType.EntryLink]: DatabaseFieldType.ENTRY_LINK,
  [FieldType.EntryDetails]: DatabaseFieldType.ENTRY_DETAILS,
  [FieldType.Backlink]: DatabaseFieldType.BACKLINK,
};

export type YJSDatabaseSubset = Pick<
  Database,
  | 'entries'
  | 'entriesById'
  | 'fields'
  | 'fieldsById'
  | 'views'
  | 'defaultViewId'
  | 'defaultEntry'
  | 'commentThreads'
  | 'meta'
>;

const convertUser = (
  values: SingleUserValue[]
): RawDatabaseValue<DatabaseFieldType.USER> => {
  if (values[0]?.type === UserType.Current) {
    return USER_VALUE_CURRENT;
  }

  const convertedUsers: RawDatabaseValue<DatabaseFieldType.USER> = [];

  values.forEach(
    (val) =>
      val.type === UserType.Referenced &&
      convertedUsers.push({
        _id: val.id,
        ...(val.accountId && { accountId: val.accountId }),
      })
  );

  return convertedUsers;
};

export const valueToDatabase = (yValue: AllValueJSON): DatabaseValue => {
  switch (yValue.type) {
    case FieldType.Text:
      return {
        type: DatabaseFieldType.TEXT,
        value: {
          plain: yValue.value.plain,
          rich: yValue.value.rich,
        },
      };
    case FieldType.Number:
    case FieldType.Date:
      return {
        type: fieldTypeMap[yValue.type] as
          | DatabaseFieldType.NUMBER
          | DatabaseFieldType.DATE,
        value: yValue.value,
      };
    case FieldType.Select:
      return {
        type: DatabaseFieldType.SELECT,
        value: yValue.value,
      };
    case FieldType.User:
      return {
        type: DatabaseFieldType.USER,
        value: convertUser(yValue.value),
      };
    case FieldType.Attachment:
      return {
        type: DatabaseFieldType.ATTACHMENT,
        value: yValue.value.map((v: SingleAttachmentValue) => ({
          fileId: v.fileId,
          _id: v.id,
        })),
      };
    case FieldType.Checkbox:
      return {
        type: DatabaseFieldType.CHECKBOX,
        value: yValue.value,
      };
    case FieldType.PageLink:
      return {
        type: DatabaseFieldType.PAGE_LINK,
        value: yValue.value.map((v) =>
          v.contentId ? { _id: v.id, contentId: v.contentId, ari: v.ari } : { _id: v.id }
        ),
      };
    case FieldType.Hyperlink:
      return {
        type: DatabaseFieldType.HYPERLINK,
        value: yValue.value.map((v: SingleHyperlinkValue) => ({
          url: v.url,
          _id: v.id,
        })),
      };
    case FieldType.EntryLink:
      return {
        type: DatabaseFieldType.ENTRY_LINK,
        value: Array.isArray(yValue.value)
          ? yValue.value.map((v: SingleEntryLinkValue) => ({
              entryId: v.entryId,
              _id: v.id,
            }))
          : [],
      };
    case FieldType.JiraIssue:
      return {
        type: DatabaseFieldType.JIRA_ISSUE,
        value: yValue.value.map((v: SingleJiraIssueValue) => ({
          key: v.key,
          _id: v.id,
        })),
      };
  }
};

export const entryToDatabase = (entryId: string, yEntry: EntryJSON): DatabaseEntry => {
  const fieldIds = Object.keys(yEntry.values);
  const values = fieldIds.map((fieldId) => valueToDatabase(yEntry.values[fieldId]));

  return {
    id: entryId,
    values: fieldIds.reduce((acc, fieldId, idx) => {
      acc[fieldId] = values[idx];
      return acc;
    }, {} as Record<string, DatabaseValue>),
  };
};

export const entriesByIdToDatabase = (
  entriesById: DatabaseJSON['entriesById']
): Database['entriesById'] => {
  const entryIds = Object.keys(entriesById);
  const entries = entryIds.map((entryId) =>
    entryToDatabase(entryId, entriesById[entryId])
  );

  return entryIds.reduce((acc, entryId, idx) => {
    acc[entryId] = entries[idx];
    return acc;
  }, {} as Database['entriesById']);
};

const displayedDetailTypeMap: Record<DisplayedDetailType, ReduxDisplayedDetailType> = {
  [DisplayedDetailType.CREATED_AT]: ReduxDisplayedDetailType.CREATED_AT,
  [DisplayedDetailType.CREATED_BY]: ReduxDisplayedDetailType.CREATED_BY,
  [DisplayedDetailType.LAST_UPDATED_AT]: ReduxDisplayedDetailType.LAST_UPDATED_AT,
  [DisplayedDetailType.LAST_UPDATED_BY]: ReduxDisplayedDetailType.LAST_UPDATED_BY,
  [DisplayedDetailType.EXCERPT_CONTENT]: ReduxDisplayedDetailType.EXCERPT,
  [DisplayedDetailType.VERSION_NUMBER]: ReduxDisplayedDetailType.CURRENT_VERSION,
};

const backlinkDisplayTypeMap: Record<BacklinkDisplayType, ReduxBacklinkDisplayType> = {
  [BacklinkDisplayType.AS_LINK]: ReduxBacklinkDisplayType.LINK,
  [BacklinkDisplayType.AS_NUMBER]: ReduxBacklinkDisplayType.NUMBER,
};

const numberFormatMap: Record<NumberFormat, ReduxNumberFormat> = {
  [NumberFormat.NUMBER]: ReduxNumberFormat.NUMBER,
  [NumberFormat.PERCENT]: ReduxNumberFormat.PERCENT,
  [NumberFormat.CUSTOM]: ReduxNumberFormat.CUSTOM,
  [NumberFormat.EURO]: ReduxNumberFormat.EURO,
  [NumberFormat.DOLLAR]: ReduxNumberFormat.DOLLAR,
  [NumberFormat.CURRENCY]: ReduxNumberFormat.CURRENCY,
};

const numberSeparatorMap: Record<NumberSeparator, ReduxNumberSeparator> = {
  [NumberSeparator.COMMA]: ReduxNumberSeparator.COMMA,
  [NumberSeparator.DOT]: ReduxNumberSeparator.DOT,
  [NumberSeparator.WHITESPACE]: ReduxNumberSeparator.WHITESPACE,
  [NumberSeparator.APOSTROPHE]: ReduxNumberSeparator.APOSTROPHE,
};

const parentPageTypeMapping: Record<ParentPageType, ReduxParentPageType> = {
  [ParentPageType.PAGE]: ReduxParentPageType.PAGE,
  [ParentPageType.AUTO]: ReduxParentPageType.AUTO,
  [ParentPageType.MACRO]: ReduxParentPageType.MACRO,
};

const getNumberFormat = (yField: FieldJSON) => {
  return {
    format: numberFormatMap[yField.numberFormat],
    minimumFractionDigits: yField.customNumberFormat.minFractionDigits,
    maximumFractionDigits: yField.customNumberFormat.maxFractionDigits,
    decimalPlaces: yField.customNumberFormat.decimalPlaces,
    prefix: yField.customNumberFormat.prefix,
    suffix: yField.customNumberFormat.suffix,
    decimalSeparator: numberSeparatorMap[yField.customNumberFormat.decimalSeparator],
    thousandsSeparator: yField.customNumberFormat.thousandsSeparator
      ? numberSeparatorMap[yField.customNumberFormat.thousandsSeparator]
      : undefined,
  };
};

export const fieldToDatabase = (
  fieldId: string,
  yField: FieldJSON
): DatabaseField<DatabaseFieldType> => {
  let pageTemplate: DatabaseFieldSettings['pageTemplate'] | undefined = undefined;
  const customTemplate = [CUSTOM_TEMPLATE_EMPTY, CUSTOM_TEMPLATE_ENTRY_MACRO].find(
    (t) => t.templateId === yField.pageTemplateId
  );
  if (customTemplate) {
    pageTemplate = customTemplate;
  } else if (yField.pageTemplateId) {
    pageTemplate = { templateId: yField.pageTemplateId };
  }

  return {
    id: fieldId,
    name: yField.name,
    description: yField.description,
    showDescription: yField.showDescription,
    type: fieldTypeMap[yField.type],
    width: yField.width,
    settings: {
      multi: yField.multi,
      resolve: yField.resolve,
      allowEdit: yField.allowEdit,
      creatable: yField.creatable,
      contentId: yField.databaseId,
      displayedDetail: yField.displayedDetail
        ? displayedDetailTypeMap[yField.displayedDetail]
        : ReduxDisplayedDetailType.CREATED_AT,
      contentField: yField.contentField,
      backlinkDisplayType: backlinkDisplayTypeMap[yField.backlinkDisplayType],
      fieldId: yField.fieldId,
      includeVersionComment: yField.includeVersionComment,
      pageSubType: yField.pageSubType,
      parentPage: yField.parentPage
        ? {
            contentId: yField.parentPage,
          }
        : undefined,
      parentPageOption: yField.parentPageOption,
      isParentPageSourceDatabase: yField.isParentPageSourceDatabase,
      parentPageType: parentPageTypeMapping[yField.parentPageType],
      pageTemplate: pageTemplate ?? CUSTOM_TEMPLATE_EMPTY,
      displayEntryOnPage: yField.displayEntryOnPage,
      selectOptions: yField.selectOptions,
      numberFormat: getNumberFormat(yField),
      mediaFormat: yField.mediaFormat,
      jiraField: yField.jiraField,
      jiraServerId: yField.jiraServerId,
      cloudId: yField.cloudId,
      lookupFieldId: yField.entryFieldField,
      defaultEnabled: yField.defaultEnabled,
      mentionNotificationEnabled: yField.mentionNotificationEnabled,
      dateFormat: yField.dateFormat,
      timeFormat: yField.timeFormat,
      enableDateRange: yField.enableDateRange,
    },
  };
};

export const fieldsByIdToDatabase = (
  fieldsById: DatabaseJSON['fieldsById']
): Database['fieldsById'] => {
  const fieldIds = Object.keys(fieldsById);
  const fields = fieldIds.map((fieldId) => fieldToDatabase(fieldId, fieldsById[fieldId]));

  const result: Database['fieldsById'] = {};
  fields.forEach((field, idx) => {
    result[field.id] = fields[idx];
  });

  return result;
};

export const collabTypeToDatabase = {
  [CollabLayoutType.TABLE]: DatabaseLayoutType.TABLE,
  [CollabLayoutType.GALLERY]: DatabaseLayoutType.GALLERY,
  [CollabLayoutType.BOARD]: DatabaseLayoutType.BOARD,
  [CollabLayoutType.CALENDAR]: DatabaseLayoutType.CALENDAR,
  [CollabLayoutType.CARD]: DatabaseLayoutType.GALLERY, // Deprecated: Card referred as Gallery
};
export const collabCardWidthToDatabase = {
  [CollabCardWidth.SMALL]: DatabaseCardWidth.SMALL,
  [CollabCardWidth.MEDIUM]: DatabaseCardWidth.MEDIUM,
  [CollabCardWidth.LARGE]: DatabaseCardWidth.LARGE,
};

export const collabCalculationTypeToDatabase = {
  [CollabCalculationTypes.SUM]: DatabaseCalculationType.SUM,
  [CollabCalculationTypes.COUNT_TOTAL]: DatabaseCalculationType.COUNT_TOTAL,
  [CollabCalculationTypes.COUNT]: DatabaseCalculationType.COUNT,
  [CollabCalculationTypes.COUNT_UNIQUE]: DatabaseCalculationType.COUNT_UNIQUE,
  [CollabCalculationTypes.COUNT_EMPTY]: DatabaseCalculationType.COUNT_EMPTY,
  [CollabCalculationTypes.PERCENT_EMPTY]: DatabaseCalculationType.PERCENT_EMPTY,
  [CollabCalculationTypes.PERCENT_NOT_EMPTY]: DatabaseCalculationType.PERCENT_NOT_EMPTY,
  [CollabCalculationTypes.AVERAGE]: DatabaseCalculationType.AVERAGE,
  [CollabCalculationTypes.MAX]: DatabaseCalculationType.MAX,
  [CollabCalculationTypes.MIN]: DatabaseCalculationType.MIN,
};

const collabFilterToDatabase = (filter: CollabFilterJSON): DatabaseFilter => {
  switch (filter.type) {
    case CollabFilterType.OR: {
      const childFilters = filter.value.map((val) => collabFilterToDatabase(val));
      return {
        id: filter.id,
        type: DatabaseFilterType.OR,
        value: childFilters,
      };
    }
    case CollabFilterType.AND: {
      const childFilters = filter.value.map((val) => collabFilterToDatabase(val));

      return {
        id: filter.id,
        type: DatabaseFilterType.AND,
        value: childFilters,
      };
    }
    case CollabFilterType.EQUALS:
      return {
        id: filter.id,
        type: DatabaseFilterType.EQUALS,
        value: valueToDatabase(filter.value),
        ...(filter.not ? { not: filter.not } : {}),
        fieldId: filter.fieldId,
      };
    case CollabFilterType.CONTAINS:
      return {
        id: filter.id,
        type: DatabaseFilterType.CONTAINS,
        fieldId: filter.fieldId,
        ...(filter.not ? { not: filter.not } : {}),
        value: filter.value,
      };
    case CollabFilterType.EMPTY:
      return {
        id: filter.id,
        type: DatabaseFilterType.EMPTY,
        fieldId: filter.fieldId,
        ...(filter.not ? { not: filter.not } : {}),
      };
    case CollabFilterType.GREATER:
      return {
        id: filter.id,
        type: DatabaseFilterType.GREATER,
        fieldId: filter.fieldId,
        orEqual: filter.orEqual,
        value: valueToDatabase(filter.value),
      };
    case CollabFilterType.LESS:
      return {
        id: filter.id,
        type: DatabaseFilterType.LESS,
        fieldId: filter.fieldId,
        orEqual: filter.orEqual,
        value: valueToDatabase(filter.value),
      };
  }
};

const collabGroupSortTypeToDatabase = {
  [CollabGroupSortType.ASCENDING]: DatabaseGroupSortType.ASCENDING,
  [CollabGroupSortType.DESCENDING]: DatabaseGroupSortType.DESCENDING,
  [CollabGroupSortType.MANUAL]: DatabaseGroupSortType.MANUAL,
};

const collabGroupingToDatabase = (collabGrouping: CollabGroupingJSON | undefined) => {
  if (!collabGrouping) {
    return undefined;
  }

  const groupBy = collabGrouping.groupBy
    ? {
        ...collabGrouping.groupBy,
        sortType: collabGroupSortTypeToDatabase[collabGrouping.groupBy.sortType],
      }
    : undefined;

  const subgroupBy = collabGrouping.subgroupBy
    ? {
        ...collabGrouping.subgroupBy,
        sortType: collabGroupSortTypeToDatabase[collabGrouping.subgroupBy.sortType],
      }
    : undefined;

  return {
    ...collabGrouping,
    groupBy,
    subgroupBy,
  };
};

const collabCalculationsToDatabase = (
  collabCalculations: Record<string, CollabCalculationTypes>
) => {
  return Object.keys(collabCalculations).reduce((acc, key) => {
    acc[key] = collabCalculationTypeToDatabase[collabCalculations[key]];
    return acc;
  }, {} as Record<string, DatabaseCalculationType>);
};

const databaseViewSettingsValidatorDefaults = databaseViewSettingsValidator.parse({});
export const collabViewToDatabaseView = (
  id: string,
  view: DatabaseViewJSON
): DatabaseView => {
  return {
    id,
    name: view.name,
    settings: {
      ...databaseViewSettingsValidatorDefaults,
      ...(view.sort && { sort: view.sort }),
      layout: {
        coverFieldId:
          view.coverField.type === CollabCoverFieldType.ID
            ? view.coverField.id
            : view.coverField.type,
        type: collabTypeToDatabase[view.layoutType],
        cardWidth: collabCardWidthToDatabase[view.cardWidth],
        ...(view.groupFieldId && { groupFieldId: view.groupFieldId }),
        ...(view.dateFieldId && { dateFieldId: view.dateFieldId }),
        showFieldNames: view.showFieldNames,
        showNumberedEntries: view.showNumberedEntries,
      },
      fields: view.fields,
      filter: view.filter ? collabFilterToDatabase(view.filter) : undefined,
      calculations: collabCalculationsToDatabase(view.calculations),
      sorts: view.sorts,
      grouping: collabGroupingToDatabase(view.grouping),
    },
  };
};

export const viewsByIdToDatabase = (
  viewsById: DatabaseJSON['viewsById'],
  views: DatabaseJSON['views']
): Database['views'] => {
  return views
    .filter((viewId) => viewsById[viewId])
    .map((viewId) => collabViewToDatabaseView(viewId, viewsById[viewId]));
};

export const convertToDatabase = (ydb: CollabDatabase): YJSDatabaseSubset => {
  const yjson = ydb.toJSON();

  return {
    fields: yjson.fields,
    fieldsById: fieldsByIdToDatabase(yjson.fieldsById),

    entries: yjson.entries,
    entriesById: entriesByIdToDatabase(yjson.entriesById),

    views: viewsByIdToDatabase(yjson.viewsById, yjson.views),
    defaultViewId: yjson.defaultViewId,
    defaultEntry: entryToDatabase(DEFAULT_ENTRY_ID, yjson.defaultEntry),
    commentThreads: yjson.commentThreads,

    meta: {
      isLocked: yjson.isLocked,
      isGlobal: undefined,
    },
  };
};

export const convertToDatabaseFromDiff = (
  diff: DatabaseDiff,
  ydb: CollabDatabase,
  base: YJSDatabaseSubset
): YJSDatabaseSubset => {
  const deletedFieldIds: string[] = [];
  const changedFieldsById = diff.changedFieldIds.reduce((acc, fieldId) => {
    if (ydb.fieldsById[fieldId]) {
      acc[fieldId] = fieldToDatabase(fieldId, ydb.fieldsById[fieldId].toJSON());
    } else {
      deletedFieldIds.push(fieldId);
    }
    return acc;
  }, {} as Record<string, DatabaseField<DatabaseFieldType>>);

  const deletedEntryIds: string[] = [];
  const changedEntriesById = diff.changedEntryIds.reduce((acc, entryId) => {
    if (ydb.entriesById[entryId]) {
      acc[entryId] = entryToDatabase(entryId, ydb.entriesById[entryId].toJSON());
    } else {
      deletedEntryIds.push(entryId);
    }

    return acc;
  }, {} as Record<string, DatabaseEntry>);

  const result: YJSDatabaseSubset = {
    ...base,

    fields: diff.fieldsOrderingChanged ? [...ydb.fields] : base.fields,
    fieldsById: {
      ...base.fieldsById,
      ...changedFieldsById,
    },

    entries: diff.entriesOrderingChanged ? [...ydb.entries] : base.entries,
    entriesById: {
      ...base.entriesById,
      ...changedEntriesById,
    },

    defaultEntry: diff.defaultEntryChanged
      ? entryToDatabase(DEFAULT_ENTRY_ID, ydb.defaultEntry.toJSON())
      : base.defaultEntry,

    views: diff.viewsChanged
      ? viewsByIdToDatabase(
          Object.keys(ydb.viewsById).reduce((acc, key) => {
            acc[key] = ydb.viewsById[key].toJSON();
            return acc;
          }, {} as Record<string, DatabaseViewJSON>),
          [...ydb.views]
        )
      : base.views,

    ...(diff.othersChanged
      ? {
          defaultViewId: ydb.defaultViewId,

          meta: {
            isLocked: ydb.isLocked,
            isGlobal: undefined,
          },
        }
      : {}),
    commentThreads: diff.threadsChanged
      ? Object.keys(ydb.commentThreads).reduce((acc, key) => {
          acc[key] = ydb.commentThreads[key].toJSON();
          return acc;
        }, {} as Record<string, CommentThreadJSON[]>)
      : base.commentThreads,
  };

  // remove deleted things
  for (const fieldId of deletedFieldIds) {
    if (result.fieldsById[fieldId]) {
      delete result.fieldsById[fieldId];
    }
  }

  for (const entryId of deletedEntryIds) {
    if (result.entriesById[entryId]) {
      delete result.entriesById[entryId];
    }
  }

  return result;
};
